
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { Send, Star } from 'lucide-react';
import { toast } from '@/utils/toast';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIsMobile } from '@/hooks/use-mobile';

interface FeedbackFormProps {
  onClose?: () => void;
}

const feedbackSchema = z.object({
  rating: z.number().min(1, "Please select a rating").max(5),
  message: z.string().min(5, "Please add a message (min 5 characters)"),
  email: z.string().email("Please enter a valid email").optional().or(z.literal(''))
});

type FeedbackFormValues = z.infer<typeof feedbackSchema>;

const FeedbackForm: React.FC<FeedbackFormProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isMobile = useIsMobile();
  
  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      rating: 0,
      message: "",
      email: ""
    }
  });
  
  const handleSubmit = async (values: FeedbackFormValues) => {
    setLoading(true);
    
    try {
      // Simulate feedback submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success("Thank you for your feedback! Your input helps us improve.");
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className={cn(
        "w-full mx-auto rounded-xl overflow-hidden shadow-lg",
        isMobile ? "max-w-[90%]" : "max-w-md", // Narrower on desktop, wider on mobile
        isDark ? "bg-[#10121E] border border-[#292D67]/30" : "bg-white border border-slate-200 text-gray-800"
      )}
    >
      <div className="relative px-5 pt-5 pb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className={cn(
            "font-bold",
            isMobile ? "text-lg" : "text-xl", // Smaller on mobile
            isDark ? "text-white" : "text-gray-800"
          )}>
            Share Your Feedback
          </h2>
          {/* Removed the duplicate exit button here */}
        </div>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="space-y-2">
              <FormLabel className={cn(
                "font-medium block mb-1 text-sm", // Reduced margin
                isDark ? "text-white/80" : "text-gray-700"
              )}>
                How was your experience?
              </FormLabel>
              <div className="flex items-center justify-center gap-1 py-1"> {/* Reduced gap and padding */}
                {[1, 2, 3, 4, 5].map((value) => {
                  const isSelected = form.watch("rating") >= value;
                  
                  return (
                    <motion.button
                      key={value}
                      type="button"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => form.setValue("rating", value, { shouldValidate: true })}
                      className={cn(
                        "p-1.5", 
                        isMobile && "p-1" // Even smaller padding on mobile
                      )}
                    >
                      <Star 
                        size={isMobile ? 24 : 28} // Smaller stars on mobile
                        className={cn(
                          "transition-colors",
                          isSelected ? "text-yellow-400" : isDark ? "text-gray-600" : "text-gray-300"
                        )}
                        fill={isSelected ? "currentColor" : "none"} 
                      />
                    </motion.button>
                  );
                })}
              </div>
              {form.formState.errors.rating && (
                <p className="text-red-500 text-xs mt-1 text-center">{form.formState.errors.rating.message}</p>
              )}
            </div>
            
            <div className="space-y-1.5"> {/* Reduced spacing */}
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={cn(
                      "font-medium text-sm",
                      isDark ? "text-white/80" : "text-gray-700"
                    )}>
                      Your feedback
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Tell us what you think about our app..."
                        className={cn(
                          "resize-none rounded-lg",
                          isMobile ? "min-h-[80px]" : "min-h-[100px]", // Smaller on mobile
                          isDark ? 
                            "bg-[#1A1D3A] border-[#2A2D50] text-white" : 
                            "bg-gray-50 border-gray-200 text-gray-800"
                        )}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="space-y-1.5"> {/* Reduced spacing */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={cn(
                      "font-medium text-sm",
                      isDark ? "text-white/80" : "text-gray-700"
                    )}>
                      Email (optional)
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="your@email.com"
                        className={cn(
                          "rounded-lg",
                          isDark ? 
                            "bg-[#1A1D3A] border-[#2A2D50] text-white" : 
                            "bg-gray-50 border-gray-200 text-gray-800"
                        )}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className={cn(
                "w-full gap-2 rounded-lg",
                isMobile ? "py-4 mt-2 text-sm" : "py-5 mt-2", // Shorter on mobile
                isDark ? 
                  "bg-gradient-to-r from-[#5B35C0] to-[#7B52F0] hover:from-[#4A2CA0] hover:to-[#6A45E0]" : 
                  "bg-gradient-to-r from-[#6C41E0] to-[#8B5CF6] hover:from-[#5B35C0] hover:to-[#7B52F0] text-white"
              )}
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span className={cn(
                    "font-medium",
                    isMobile ? "text-xs" : "text-sm" // Smaller text on mobile
                  )}>Submitting...</span>
                </>
              ) : (
                <>
                  <Send size={isMobile ? 14 : 16} />
                  <span className={cn(
                    "font-medium",
                    isMobile ? "text-xs" : "text-sm" // Smaller text on mobile
                  )}>Submit Feedback</span>
                </>
              )}
            </Button>
          </form>
        </Form>
      </div>
      
      <div className={cn(
        "px-5 py-2.5 mt-2 text-center", // Reduced spacing
        isMobile ? "text-[10px]" : "text-xs", // Smaller text on mobile
        isDark ? "bg-[#171941] text-gray-300" : "bg-gray-50 text-gray-500"
      )}>
        <p>
          We appreciate your feedback! It helps us improve Chatrix for everyone.
        </p>
      </div>
    </motion.div>
  );
};

export default FeedbackForm;
